// Read-only selectors for the UI (pure functions — they never mutate state).
import { eqDef, fieldDef, regionDef, rigStats, bitStats, stat, unitPrice, eraDollars, fieldTransports, transportStats, isAvailable, yearOf, defsByCat, EQUIPMENT } from './defs.js';
import { strataAt, stratumAt, reservoirsAt } from './geo.js';
import { ropAt, crewFactors, wobFactors, enginePower, WOB_MIN, WOB_MAX, stimCostOf } from './drilling.js';
export { stimCostOf }; // Well panel: price on the stimulation buttons
import { activeRigCrew, crewWage, roleName, TRAIT_NAMES } from './crew.js';
import { storageView, netback, transportCapacity, transportCostPerBbl, resState, LIFT_THRESHOLD } from './production.js';
import { leasePrice, fieldOpen, boomFactor } from './survey.js';
import { netWorth as nw, netWorth2025 as nw25, creditLine as cl, creditInfo as ci, monthNet, assetValue, debt } from './finance.js';
import { regionView as rv, attackChance, guardFactor } from './security.js';
import { researchView as resv, isUnlocked as unl } from './research.js';
import { budgetMitigation, rigSafety, INCIDENTS, incidentChance } from './safety.js';
import { boilerView, boilerFuelMult } from './boiler.js';
import { rushCost, balance, remainingDays } from '../economy/tokens.js';
import { spotPrice } from '../economy/market.js';
export { contractsView } from './contracts.js';
export { cosmeticsView } from '../economy/cosmetics.js';
// M2 rotary drilling views (sim/m2views.js)
import { rotaryWellView, rotaryCommands, rigM2View, rotaryWobCurve } from './m2views.js';
export { pipeView, fieldRigsView } from './m2views.js';
// M3 offshore operations views (sim/m3/opsviews.js)
import { fieldM3View, rigM3View, wellM3View, plotWaters } from './m3/opsviews.js';
import { shaleLateralView } from './m4/shaleViews.js'; // M4-A: wellView().lateral
export { platformsView, platformQuote, logisticsView, charterQuote, charterBoardView, eorView, megaprojectsView, rigFitView } from './m3/opsviews.js';
// M2 economy & politics views (regionView already carries the M2 politics block)
export { prorationView } from './m2/proration.js';
export { taxView } from './m2/taxes.js';
export { surveyView, wellLogView } from './m2/geophysics.js';
export { licensingView } from './m2/licensing.js';
import { refineryView as refv, refineriesView as refsv, refinerySitesView as rsites, productMarketsView as pmv,
  marginView as mv, fireTestView as ftv } from './refining.js';

export const netWorth = (state) => nw(state);
export const netWorth2025 = (state) => nw25(state);
export const creditLine = (state) => cl(state);           // number: credit available now
export const creditInfo = (state) => ci(state);           // { limit, used, available, rate }
export const regionView = (state, regionId) => rv(state, regionId);
export const researchView = (state) => resv(state);
export const isUnlocked = (state, defId) => unl(state, defId);
export const tokenBalance = (state) => balance(state);

export function monthlyPL(state) {
  const cur = state.finance.current ? [{ ...state.finance.current, net: monthNet(state.finance.current), current: true }] : [];
  return [...state.finance.history, ...cur];
}

// ---- well ------------------------------------------------------------------------------
function rigDailyCost(state, rig) {
  let c = 0;
  for (const m of activeRigCrew(state, rig)) c += crewWage(state, m);
  if (rig.engineUid && state.items[rig.engineUid]) {
    const e = eqDef(state.items[rig.engineUid].defId);
    c += eraDollars(state, stat(e, 'fuelPerDay', 0), e.baseYear ?? e.year) * boilerFuelMult(state, rig);
  }
  c += rigSafety(rig).budget ?? 0;
  return c;
}

// Cost/ft and speed at a given WOB for the current stratum (DESIGN §14 readouts).
function wobProjection(state, well, rig, wob, s, dailyCost) {
  const bit = state.items[rig.bitUid];
  if (!bit) return { wob, rop: 0, costPerFt: null, risk: 0, bitDays: 0 };
  const bdef = eqDef(bit.defId);
  const bs = bitStats(bdef);
  const saved = well.wob;
  well.wob = wob; // temporarily (restored below) — ropAt reads well.wob
  const rop = ropAt(state, well, s);
  well.wob = saved;
  const cf = crewFactors(state, rig);
  const wf = wobFactors(state, rig, wob, bdef, s.hard, cf);
  const wearPerFt = s.hard / bs.life * 100 * (s.hard > bs.maxHard ? 3 : 1) * wf.wearMult;
  // below the trip-out point with nothing to dress or swap to, the crew runs the dull bit to destruction: count what's left
  const trip = rig.auto.tripBelowPct ?? 15, dull = bit.condition <= trip + 0.5;
  const usable = Math.max(0.5, dull ? bit.condition : bit.condition - trip);
  const fullRun = Math.max(1, 100 - (rig.auto.tripBelowPct ?? 15));
  const ftPerRun = fullRun / Math.max(wearPerFt, 1e-6);
  const drillDays = rop > 0 ? ftPerRun / rop : Infinity;
  const tripDays = 1;
  const bitCost = unitPrice(state, bdef) / 3; // a bit survives ~3 dressings
  const snapP = 0.0015 * wf.riskMult;
  const fishCostPerDay = snapP * 6.5 * dailyCost;
  const costPerFt = rop > 0 ? (bitCost + (dailyCost + fishCostPerDay) * (drillDays + tripDays)) / ftPerRun : null;
  return {
    wob, rop, costPerFt, risk: wf.riskMult, founder: wf.founder, dull,
    bitDays: rop > 0 ? (usable / wearPerFt) / rop : Infinity,
  };
}

// Driller's time: the cheapest-cost-per-foot weight band for the rock the bit is in and the next one down
// (driving through a boundary mid-shift needs the next band), plus where the bit starts to bounce.
function sweetBands(state, well, rig, strata, daily) {
  const at = strata.findIndex((s) => well.depth + 0.01 >= s.top && well.depth + 0.01 < s.bottom);
  const pick = at < 0 ? [strata.at(-1)] : strata.slice(at, at + 2);
  const bs = rig.bitUid ? bitStats(eqDef(state.items[rig.bitUid]?.defId)) : { maxHard: 9 };
  return pick.filter(Boolean).map((s) => {
    const pts = [];
    for (let w = WOB_MIN; w <= WOB_MAX + 1e-9; w += 0.05) { const p = wobProjection(state, well, rig, Math.round(w * 100) / 100, s, daily); if (Number.isFinite(p.costPerFt)) pts.push(p); }
    if (!pts.length) return null;
    const best = pts.reduce((a, b) => (b.costPerFt < a.costPerFt ? b : a));
    // a tight band round the cheapest weight: the lever has to move when the rock changes
    const lo = Math.max(WOB_MIN, Math.round((best.wob - 0.1) * 100) / 100), hi = Math.min(WOB_MAX, Math.round((best.wob + 0.1) * 100) / 100);
    const hard = s.hard ?? 3;
    const bounceAt = hard >= 5 || hard > bs.maxHard ? Math.min(WOB_MAX, best.wob + (hard > bs.maxHard ? 0.15 : 0.25)) : null;
    return { top: s.top, bottom: s.bottom, name: s.name ?? s.rock, hard, best: best.wob, lo, hi, bounceAt, tooHard: hard > bs.maxHard };
  }).filter(Boolean);
}

export function wellView(state, wellId) {
  const well = state.wells[wellId];
  if (!well) return null;
  const fdef = fieldDef(well.fieldId);
  const field = state.fields[well.fieldId];
  const plot = field.plots[well.plot];
  // Geology the company knows; the well view hides the rest below the bit. Other wells in this field show the column
  // down to their depth ("expected", from offset wells); a seismic survey of the field shows layer boundaries only.
  let offsetTo = 0;
  for (const o of Object.values(state.wells)) if (o !== well && o.fieldId === well.fieldId && o.depth > offsetTo) offsetTo = o.depth;
  const seismic = (field.surveysDone ?? []).some((id) => /seismic|ocean_bottom|fwi/.test(id));
  const strata = strataAt(fdef, plot).map((s) => ({ ...s, known: s.top < well.depth + 0.01, expected: s.top >= well.depth + 0.01 && s.top < offsetTo }));
  const cur = stratumAt(strata, well.depth + 0.01);
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  const bitItem = rig?.bitUid ? state.items[rig.bitUid] : null;
  const allRes = reservoirsAt(fdef, plot);
  const reservoirs = allRes.filter((r) => well.revealed.includes(r.id)).map((r) => ({ ...r, state: field.reservoirs[r.id] }));
  const timer = well.timerId ? state.timers[well.timerId] : null;
  let wob = null, costs = { total: well.cost, perDay: 0 };
  if (rig) {
    const daily = rigDailyCost(state, rig);
    costs.perDay = daily;
    if (bitItem && ['DRILLING', 'PAUSED', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'RIGGING_UP', 'GAS_BLOW'].includes(well.status)) {
      const now = wobProjection(state, well, rig, well.wob ?? 1, cur, daily);
      const curve = [];
      for (let w = WOB_MIN; w <= WOB_MAX + 1e-9; w += 0.1) {
        const p = wobProjection(state, well, rig, Math.round(w * 10) / 10, cur, daily);
        curve.push({ wob: p.wob, rop: p.rop, costPerFt: p.costPerFt, risk: p.risk });
      }
      const remaining = Math.max(0, well.target - well.depth);
      const riskLevel = now.risk < 1.05 ? 'low' : now.risk < 1.6 ? 'medium' : now.risk < 2.2 ? 'high' : 'extreme';
      wob = {
        value: well.wob ?? 1, min: WOB_MIN, max: WOB_MAX, stops: { Light: 0.6, Normal: 1.0, Heavy: 1.3, 'Ram it': 1.6 },
        projectedRop: now.rop, bitDaysLeft: now.bitDays, bitDull: !!now.dull, costPerFt: now.costPerFt,
        daysToTarget: now.rop > 0 ? remaining / now.rop : null, riskMult: now.risk, riskLevel,
        founderPoint: now.founder, deviationDeg: well.deviationDeg ?? 0, costCurve: curve,
        bands: sweetBands(state, well, rig, strata, daily), // driller's time: sweet spot per rock (this one & the next)
      };
    }
  }
  const res = resState(state, well);
  const m2 = rotaryWellView(state, well, rig, cur, costs.perDay);
  if (wob && m2.rpm) Object.assign(wob, rotaryWobCurve(state, well, cur, costs.perDay));
  else if (wob) { // cable tools: one RPM column (the stroke tempo is part of WOB)
    const best = wob.costCurve.filter((p) => p.costPerFt != null).sort((a, b) => a.costPerFt - b.costPerFt)[0] ?? null;
    wob.costGrid = { wob: wob.costCurve.map((p) => p.wob), rpm: [1], cost: wob.costCurve.map((p) => [p.costPerFt]), best: best ? { wob: best.wob, rpm: 1, costPerFt: best.costPerFt, rop: best.rop } : null };
  }
  return {
    well, field: { def: fdef, state: field }, plot,
    strataAtPlot: strata, currentStratum: cur, geo: { knownTo: well.depth, expectedTo: Math.max(well.depth, offsetTo), seismic },
    rop: rig ? ropAt(state, well, cur) : 0,
    rig: rig ? { uid: rig.uid, name: rig.name, defId: rig.defId, def: eqDef(rig.defId), condition: rig.condition, tours: rig.tours, auto: rig.auto, line: rig.line, engine: rig.engineUid ? state.items[rig.engineUid] : null, crew: activeRigCrew(state, rig), maxDepth: rigStats(eqDef(rig.defId)).maxDepth, safety: rigSafety(rig), boiler: boilerView(state, rig) } : null,
    bit: bitItem ? { ...bitItem, def: eqDef(bitItem.defId) } : null,
    casing: well.casing, shoe: well.shoe,
    openHole: { from: well.shoe, to: well.depth, unstable: strata.filter((s) => s.known && well.shoe < s.bottom && s.stableDays < 30).map((s) => ({ rock: s.rock, top: s.top, daysOpen: well.passed[s.idx] != null ? state.day - well.passed[s.idx] : 0, stableDays: s.stableDays })) },
    pressure: { pore: Math.max(...strata.filter((s) => s.known && s.bottom > well.shoe).map((s) => s.pore ?? 8.6), 8.4), frac: Math.min(...strata.filter((s) => s.known && s.bottom > well.shoe).map((s) => s.frac ?? 14), 20), mw: well.mud?.ppg ?? 8.4 },
    reservoirs, problem: well.problem,
    prod: well.prod ? { ...well.prod, pressure: res?.pressure ?? null, needsLift: (res?.pressure ?? 1) < LIFT_THRESHOLD, reservoir: res } : null,
    gusher: well.gusher,
    timer: timer ? { ...timer, rushCost: rushCost(state, timer), halfRushCost: rushCost(state, timer, { half: true }), remainingDays: remainingDays(state, timer) } : null,
    costs, wob,
    security: { guardFactor: guardFactor(state, well), attackChance: attackChance(state, well), guards: well.security?.guards ?? [], fort: well.security?.fort ?? null },
    ...m2,
    ...(m2.pressureWindow ? { pressure: { pore: m2.pressureWindow.pore, frac: m2.pressureWindow.frac, mw: m2.pressureWindow.mw } } : {}),
    commands: { ...wellCommands(state, well, rig), ...rotaryCommands(state, well, rig) },
    m3: wellM3View(state, well), // M3 ops: water depth, sea state, storm, platform/subsea, riser
    lateral: well.lateral ? shaleLateralView(state, well) : null, // M4-A: L-shaped well view (horizontal shale wells)
  };
}

function wellCommands(state, well, rig) {
  const s = well.status;
  if (well.ownerOperatorId) return contractCommands(baseCommands(state, well, rig, s));
  return baseCommands(state, well, rig, s);
}
// Contract wells (§17): drilling controls only; the operator owns completion, plugging & production.
const contractCommands = (c) => ({ ...c, complete: false, deepen: false, plug: false, torpedo: false, lift: false, shutIn: false, reopen: false, release: false });
function baseCommands(state, well, rig, s) {
  return {
    drill: !!rig && (s === 'PAUSED' || ((s === 'AT_TD' || s === 'DRY') && well.target > well.depth)),
    pause: s === 'DRILLING',
    changeBit: !!rig && ['DRILLING', 'PAUSED'].includes(s),
    dressBit: !!rig && ['DRILLING', 'PAUSED'].includes(s),
    runCasing: !!rig && ['DRILLING', 'PAUSED', 'AT_TD', 'DRY'].includes(s) && well.depth - well.shoe >= 1,
    complete: s === 'AT_TD' && !!well.reservoirId,
    deepen: ['DRY', 'AT_TD', 'SUSPENDED'].includes(s),
    plug: !['PLUGGED', 'GUSHER'].includes(s),
    torpedo: !!well.reservoirId && ['AT_TD', 'PRODUCING', 'SHUT_IN'].includes(s),
    lift: !!well.prod && ['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(s) && !well.lift,
    shutIn: s === 'PRODUCING',
    reopen: s === 'SHUT_IN',
    release: !!rig && s !== 'GUSHER',
    straighten: !!rig && (well.deviationDeg ?? 0) >= 3 && ['DRILLING', 'PAUSED'].includes(s),
  };
}

// ---- field -----------------------------------------------------------------------------
export function fieldView(state, fieldId) {
  const def = fieldDef(fieldId);
  const f = state.fields[fieldId];
  if (!def || !f) return null;
  const open = fieldOpen(state, fieldId);
  const y = yearOf(state);
  const waters = plotWaters(fieldId); // M3 ops: per-plot water depth (null on land)
  const plots = f.plots.map((p, idx) => {
    const w = p.wellId ? state.wells[p.wellId] : null;
    return {
      idx, x: p.x, y: p.y, seep: p.seep, owned: p.owned,
      leasePrice: open && !p.owned && !p.wellId && !p.taken ? leasePrice(state, fieldId, idx) : null,
      taken: p.taken ? { by: p.taken.by, day: p.taken.day, drilled: !!p.taken.well, struck: !!p.taken.well && !p.taken.well.dry } : null,
      sellBack: p.owned && !p.wellId && open ? Math.round(leasePrice(state, fieldId, idx) * 0.7) : null,
      est: p.survey?.est ?? null, sigma: p.survey?.sigma ?? null, methods: p.survey?.methods ?? [],
      publicEst: p.pubEst, priceMult: p.priceMult,
      leaseExpires: p.leaseExpires, wellId: p.wellId, wellStatus: w?.status ?? null, wellName: w?.name ?? null,
      rate: w?.prod?.rate ?? 0, depth: w?.depth ?? 0,
      startPlot: def.startPlot === idx,
      ...(waters ? { waterDepthFt: waters[idx] } : {}), // M3 ops
    };
  });
  const tdef = f.transportId ? eqDef(f.transportId) : null;
  const transports = fieldTransports(def).map((t) => {
    const d = eqDef(t.id);
    const ts = transportStats(d);
    return { id: t.id, name: d?.name ?? t.id, year: Math.max(t.year, d?.year ?? 0), available: !!d && y >= t.year && isAvailable(d, state), connected: f.connected.includes(t.id), connectCost: d ? eraDollars(state, ts.connectCost, d.baseYear ?? d.year) : 0, costPerBbl: ts.costPerBbl, capacityPerDay: ts.capacityPerDay, current: t.id === f.transportId };
  });
  return {
    def, open, opensDay: def.opensDay, boomFactor: boomFactor(state, fieldId), plots,
    grid: def.grid, region: regionDef(def.regionId),
    storage: { ...storageView(state, fieldId), tanks: Object.values(state.items).filter((i) => i.loc.type === 'field' && i.loc.id === fieldId && eqDef(i.defId)?.cat === 'storage') },
    transport: { current: tdef, capacityPerDay: transportCapacity(state, fieldId), costPerBbl: transportCostPerBbl(state, fieldId), options: transports, shipQueue: f.shipQueue },
    netback: netback(state, fieldId), sellMode: f.sellMode, autoBuyBarrels: f.autoBuyBarrels !== false,
    surveysDone: f.surveysDone ?? [],
    surveysRunning: Object.values(state.timers).filter((t) => t.kind === 'SURVEY' && t.refId === fieldId),
    wells: Object.values(state.wells).filter((w) => w.fieldId === fieldId).map((w) => ({ id: w.id, name: w.name, status: w.status, rate: w.prod?.rate ?? 0, depth: w.depth })),
    production: Object.values(state.wells).filter((w) => w.fieldId === fieldId).reduce((a, w) => a + (w.prod?.rate ?? 0), 0),
    security: f.security,
    ...fieldM3View(state, fieldId), // M3 ops: offshore, environment, sea, storm, platforms, season
  };
}

// ---- misc views ------------------------------------------------------------------------
export function rigsView(state) {
  return Object.values(state.rigs).map((r) => {
    const def = eqDef(r.defId);
    const well = r.wellId ? state.wells[r.wellId] : null;
    const rs = rigStats(def);
    const crew = activeRigCrew(state, r);
    const missing = Object.entries(rs.requiredRoles ?? {}).filter(([role, n]) => crew.filter((c) => c.role === role).length < n).map(([role]) => role);
    return { ...r, def, well: well ? { id: well.id, name: well.name, status: well.status, depth: well.depth } : null, crew, missingRoles: missing, power: enginePower(state, r), boiler: boilerView(state, r), maxDepth: rs.maxDepth, dailyCost: rigDailyCost(state, r), budgetMitigation: budgetMitigation(state, rigSafety(r).budget), ...rigM2View(state, r), ...rigM3View(state, r) };
  });
}

export function crewView(state) {
  const roster = Object.values(state.crew).filter((c) => c.status === 'active').map((c) => ({ ...c, roleName: roleName(c.role), traitNames: c.traits.map((t) => TRAIT_NAMES[t] ?? t), wageNow: crewWage(state, c), rigName: c.rigId ? state.rigs[c.rigId]?.name : null }));
  return { roster, hirePool: state.hirePool.map((c) => ({ ...c, roleName: roleName(c.role), traitNames: c.traits.map((t) => TRAIT_NAMES[t] ?? t) })), wageMult: state.company.wageMult, memorial: state.memorial ?? [] };
}

export function timersView(state) {
  return Object.values(state.timers).map((t) => ({ ...t, remainingDays: remainingDays(state, t), rushCost: rushCost(state, t), halfRushCost: rushCost(state, t, { half: true }) }));
}

export function decisionsView(state) {
  return { current: state.pendingDecision, queued: state.decisionQueue ?? [] };
}

export function safetyView(state) {
  return {
    warnings: Object.values(state.safety.warnings),
    daysWithoutIncident: state.safety.daysWithoutIncident,
    memorial: state.memorial ?? [],
    workersComp: state.safety.workersComp,
    rigs: Object.values(state.rigs).map((r) => ({ uid: r.uid, name: r.name, ...rigSafety(r), mitigation: budgetMitigation(state, rigSafety(r).budget) })),
    incidentTypes: INCIDENTS.map((i) => ({ id: i.id, name: i.name, seen: !!state.safety.seen[i.id] })),
    gear: defsByCat('safety').map((d) => ({ id: d.id, name: d.name, year: d.year, price: unitPrice(state, d), available: isAvailable(d, state), mitigates: stat(d, 'mitigates', {}) })),
  };
}

export function shopView(state, cat = null) {
  const y = yearOf(state);
  return Object.values(EQUIPMENT).filter((d) => (!cat || d.cat === cat) && unl(state, d.id) && !(d.obsoleteYear && y >= d.obsoleteYear))
    .map((d) => ({ def: d, price: unitPrice(state, d), isNew: y - d.year <= 1 && d.year > 1859, researched: !!state.research?.unlockedDefs?.[d.id] && y < d.year, inStock: state.stock[d.id]?.qty ?? 0 }));
}

export function companyView(state) {
  const spot = spotPrice(state);
  return {
    name: state.company.name, cash: state.company.cash, reputation: state.company.reputation,
    netWorth: nw(state), netWorth2025: nw25(state), assets: assetValue(state), debt: debt(state), credit: ci(state),
    loans: state.company.loans, stats: state.company.stats, spot, tokens: balance(state), eraId: state.eraId,
    production: Object.values(state.wells).reduce((a, w) => a + (w.prod?.rate ?? 0), 0),
    stored: Object.values(state.fields).reduce((a, f) => a + f.storage.bbl, 0),
  };
}

// ---- refining (DESIGN §19)
export const refineryView = (state, refUid) => refv(state, refUid);
export const refineriesView = (state) => refsv(state);
export const refinerySitesView = (state) => rsites(state);
export const productMarketsView = (state) => pmv(state);
export const marginView = (state, refUid, overrides) => mv(state, refUid, overrides);
export const fireTestView = (state, refUid, degF) => ftv(state, refUid, degF);

// ---- daily burn & break-even (sim/austerity.js)
export { burnView } from './austerity.js';

// ---- M3 economy & politics (sim/m3/econviews.js)
export { leaseRoundView, concessionView, hedgeView, routeView, regimeView, nationalizationView, fiscalView, opecView } from './m3/econviews.js';

// M4 (2000–2029) view models
export * from './m4/views.js';
